import { AssistantAnswers, AssistantHistoryTurn, AssistantMode, AssistantReply } from '../types/ai.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export interface StreamStep {
  id: string;
  label: string;
  state?: 'run' | 'done' | 'wait';
  details?: string[];
  ok?: boolean;
}

export type AssistantPayload = {
  message: string;
  mode?: AssistantMode;
  history?: AssistantHistoryTurn[];
  page?: string;
  thread_id?: string;
  answers?: AssistantAnswers;
};

export type SseEvent = { event: string; data: unknown };

export class AssistantStreamError extends Error {
  code: string;
  response?: { status: number; data: { error: { code: string; message: string } } };

  constructor(code: string, message: string, status = 500) {
    super(message);
    this.code = code;
    this.response = {
      status,
      data: { error: { code, message } }
    };
  }
}

function statusFor(code: string): number {
  if (code === 'AI_PROMPT_SHORT' || code === 'AI_PROMPT_LONG') return 400;
  if (code === 'AI_INACTIVE') return 409;
  if (code === 'AI_QUOTA_EXCEEDED') return 429;
  if (code === 'AI_MODEL_FORBIDDEN') return 403;
  return 500;
}

export function parseSseBlock(block: string): SseEvent | null {
  let event = 'message';
  const lines: string[] = [];
  String(block || '').split('\n').forEach((line) => {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) lines.push(line.slice(5).trimStart());
  });
  if (!lines.length) return null;
  const raw = lines.join('\n');
  try {
    return { event, data: JSON.parse(raw) };
  } catch {
    return { event, data: raw };
  }
}

export function parseSseBuffer(buffer: string): { events: SseEvent[]; rest: string } {
  const chunks = String(buffer || '').split('\n\n');
  const rest = chunks.pop() || '';
  return { events: chunks.map(parseSseBlock).filter((item): item is SseEvent => Boolean(item)), rest };
}

export function shouldFallbackToPost(error: unknown): boolean {
  const err = error as { code?: string; response?: { status?: number } };
  if (err && err.code === 'STREAM_INCOMPLETE') return true;
  const status = err && err.response && err.response.status;
  if (!status) return true;
  if (status === 404 || status >= 500) return true;
  return false;
}

function raiseFromBody(body: { error?: { code?: string; message?: string } }, status: number): never {
  const code = body && body.error && body.error.code ? body.error.code : 'AI_ERROR';
  const message = body && body.error && body.error.message ? body.error.message : 'Indisponible';
  throw new AssistantStreamError(code, message, status);
}

export async function consumeAssistantStream(
  stream: ReadableStream<Uint8Array>,
  handlers: { onStep?: (step: StreamStep) => void; onToken?: (text: string) => void }
): Promise<AssistantReply> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let final: AssistantReply | null = null;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parsed = parseSseBuffer(buf);
      buf = parsed.rest;
      for (const event of parsed.events) {
        if (event.event === 'step' && event.data && typeof event.data === 'object') {
          handlers.onStep?.(event.data as StreamStep);
        } else if (event.event === 'token' && event.data && typeof event.data === 'object') {
          const text = (event.data as { text?: string }).text;
          if (text) handlers.onToken?.(text);
        } else if (event.event === 'final' && event.data && typeof event.data === 'object') {
          final = event.data as AssistantReply;
        } else if (event.event === 'error' && event.data && typeof event.data === 'object') {
          const payload = event.data as { code?: string; message?: string };
          const code = payload.code || 'AI_ERROR';
          throw new AssistantStreamError(code, payload.message || 'Erreur IA', statusFor(code));
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
  if (!final) {
    const err = new AssistantStreamError('STREAM_INCOMPLETE', 'Flux coupé', 502);
    throw err;
  }
  return final;
}

export async function fetchAssistantStream(
  storeId: number,
  payload: AssistantPayload,
  handlers: { onStep?: (step: StreamStep) => void; onToken?: (text: string) => void } = {}
): Promise<AssistantReply> {
  const token = localStorage.getItem('accessToken');
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 60000);
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/stores/${storeId}/ai/assistant/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
  } catch (error) {
    const err = error as { name?: string };
    if (err && err.name === 'AbortError') {
      throw new AssistantStreamError('STREAM_INCOMPLETE', 'Délai dépassé', 502);
    }
    throw error;
  } finally {
    window.clearTimeout(timer);
  }

  const type = response.headers.get('content-type') || '';
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    raiseFromBody(body, response.status);
  }
  if (!type.includes('text/event-stream') || !response.body) {
    throw new AssistantStreamError('STREAM_INCOMPLETE', 'Flux indisponible', 502);
  }
  return consumeAssistantStream(response.body, handlers);
}

export async function assistantWithFallback(
  stream: (storeId: number, payload: AssistantPayload) => Promise<AssistantReply>,
  post: (storeId: number, payload: AssistantPayload) => Promise<AssistantReply>,
  storeId: number,
  payload: AssistantPayload
): Promise<AssistantReply> {
  try {
    return await stream(storeId, payload);
  } catch (error) {
    if (!shouldFallbackToPost(error)) throw error;
    return post(storeId, payload);
  }
}
